import {type ChartAggOption} from '../../../generated/graphql';
import {BIN_POINT_TO_USE} from './../../../util/plotHelpers/chartTypes';
import {
  type HistoryAggKey,
  type HistoryFallThroughKey,
  type HistoryRecord,
  type Point,
  type PointType,
  type ValidHistoryKey,
} from './types';

export type XKeyPositions = 'min' | 'avg' | 'last' | 'max';

const XKeymap: Record<XKeyPositions, PointType> = {
  avg: 'Avg',
  last: 'Last',
  max: 'Max',
  min: 'Min',
};

/**
 * This function returns the correct x-axis key for a given metric. It maps a metric to the value in the HistoryPoint with the correct suffix.
 *
 * If the y-axis metric is a system metric then the x-axis metric will be returned unmodified.
 */
export function getSafeXKey({
  xMetricKey,
  position = 'last',
  record,
}: {
  xMetricKey: string;
  record: HistoryRecord;
  position?: XKeyPositions;
}): ValidHistoryKey {
  const ffKey = `${xMetricKey}${XKeymap[position]}`;
  if (ffKey in record) {
    return ffKey as ValidHistoryKey;
  }
  return xMetricKey as ValidHistoryKey;
}
/**
 * This map exists to translate groupAgg options into suffixes that can be used to read data out of the api returned bins.
 *
 * A bin returns `-Min, -Max, -Last, and -Avg suffixed values for each metric. This translates the groupAgg option into the correct suffix so that the line construction logic automatically pulls the correct option.
 *
 * e.g. if you're constructing a group and you want to show all the max values you'd choose the max groupAgg option, and then when the buckets are reduced into the line during the grouping function the max value would be returned instead of the average or min value.
 */
export const mapGroupAggToSuffix: Record<ChartAggOption, PointType> = {
  mean: 'Avg',
  min: 'Min',
  max: 'Max',
  last: 'Last', //
  // these options aren't supported yet and won't be called
  // they're just here to satisfy the types for now
  median: 'Avg',
  sum: 'Avg',
  samples: 'Avg',
};

/**
 * If we're plotting a system metric plot (only usage currently known) on the y-axis we need to avoid adding the `Avg` suffix to the key because the API doesn't return these values in binned mode. This function takes in the yMetric (and an optional xMetric if needing to safely access that as well and returns prefixed or unprefixed keys as needed.)
 */
export function getSafeYKey({
  yMetricKey,
  overrides,
  aggType = BIN_POINT_TO_USE.toLowerCase() as ChartAggOption,
  record,
}: {
  yMetricKey: string;
  overrides?: {
    yAxisSuffix?: string;
  };
  aggType?: ChartAggOption;
  record: HistoryRecord;
}): ValidHistoryKey {
  // if we're doing a system metric plot we get results back that aren't binned
  // we can handle that by avoid transforming logic to use the `-Avg` suffix
  const suffix = mapGroupAggToSuffix[aggType] ?? BIN_POINT_TO_USE;
  const ySuffix = overrides?.yAxisSuffix ?? suffix;
  const ffKey = `${yMetricKey}${ySuffix}`;

  if (record && ffKey in record) {
    return ffKey as ValidHistoryKey;
  }

  return yMetricKey as ValidHistoryKey;
}

export function sortPointsByX(a: Point, b: Point) {
  return a.x - b.x;
}
export function takeLastDuplicate(point: Point, index: number, array: Point[]) {
  return index === array.length - 1 || array[index + 1].x !== point.x;
}

export function getAreaSafeYKeys(
  yMetricKey: string,
  record: HistoryRecord
): {
  minKey: HistoryAggKey | HistoryFallThroughKey;
  maxKey: HistoryAggKey | HistoryFallThroughKey;
  avgKey: HistoryAggKey | HistoryFallThroughKey;
  stdDevKey: HistoryAggKey | HistoryFallThroughKey;
  stdErrKey: HistoryAggKey | HistoryFallThroughKey;
} {
  const useDefault = !recordHasKey(record, yMetricKey);
  const yMinSuffix = useDefault ? '' : 'Min';
  const yMaxSuffix = useDefault ? '' : 'Max';
  const yAvgSuffix = useDefault ? '' : 'Avg';
  const yStdDevSuffix = useDefault ? '' : 'StdDev';
  const yStdErrSuffix = useDefault ? '' : 'StdErr';
  return {
    minKey: `${yMetricKey}${yMinSuffix}` as ValidHistoryKey,
    maxKey: `${yMetricKey}${yMaxSuffix}` as ValidHistoryKey,
    avgKey: `${yMetricKey}${yAvgSuffix}` as ValidHistoryKey,
    stdDevKey: `${yMetricKey}${yStdDevSuffix}` as ValidHistoryKey,
    stdErrKey: `${yMetricKey}${yStdErrSuffix}` as ValidHistoryKey,
  };
}

export function sliceObjectKeys<T extends Record<string, any>>(
  obj: T,
  numKeys: number
) {
  return Object.entries(obj).reduce((acc, [key, val], index) => {
    if (index < numKeys) {
      acc[key as keyof T] = val;
    }
    return acc;
  }, {} as Partial<T>);
}

export function recordHasKey(record: HistoryRecord, key: string) {
  return (
    `${key}Last` in record ||
    `${key}Min` in record ||
    `${key}Max` in record ||
    `${key}Avg` in record ||
    `${key}StdDev` in record ||
    `${key}StdErr` in record
  );
}
